/**
 * LOD transition hysteresis (PR-J).
 *
 * # Why hysteresis
 *
 * Without hysteresis, a small zoom jitter that crosses a boundary
 * (e.g. 1.99 → 2.01 px) flips the bucket back and forth, producing
 * visible pop / flicker. AutoCAD parity requires the visual
 * progression on continuous zoom to feel monotonic — once a fence is
 * crossed, the user does not see the previous representation again
 * until they zoom further past it.
 *
 * # Strategy: 10% buffer around each boundary
 *
 * For every threshold `T` (0.5 / 2 / 8 px) we define a band
 * `[T × 0.9, T × 1.1]`. Inside the band we keep the *previous*
 * bucket — the boundary classifier (`lodBucketForEntity`) is only
 * trusted outside the band:
 *
 *     pixels < T × 0.9  → bucket below T
 *     pixels > T × 1.1  → bucket above T
 *     pixels in band    → previous bucket (sticky)
 *
 * Implementation note: when the previous bucket is `undefined` (very
 * first classification), we fall back to the boundary classifier —
 * there is nothing to be sticky against on the first frame.
 *
 * # API shape
 *
 * The hysteresis layer is stateless from the caller's point of view —
 * the previous bucket is passed in explicitly. This makes the layer
 * cheap to call from a `useMemo` / pure recompute that has no React
 * lifetime, and keeps unit tests one-shot.
 */

import {
  type LodBucket,
  LOD_THRESHOLDS_PX,
  entityDrawsDegenerateMarker,
  entityScreenSizePx,
  isDecoratorEntityType,
  isDegenerateBounds,
  lodBucketForBounds,
  lodBucketForPixels,
} from "./lodPolicy";
import type { RenderableEntity } from "../types";

/** Half-width of the hysteresis band, expressed as a fraction of T. */
export const HYSTERESIS_FRACTION = 0.1;

/**
 * Numeric boundaries in pixels that separate "definitely bucket A"
 * from "definitely bucket B", with the hysteresis band in between.
 *
 * `lower[T]` = `T × (1 - HYSTERESIS_FRACTION)` (below this — bucket
 * below T).
 * `upper[T]` = `T × (1 + HYSTERESIS_FRACTION)` (above this — bucket
 * above T).
 *
 * Frozen so callers can introspect / display the band without
 * accidentally mutating it.
 */
export const HYSTERESIS_BANDS = Object.freeze({
  skipLower: LOD_THRESHOLDS_PX.skip * (1 - HYSTERESIS_FRACTION), // 0.45
  skipUpper: LOD_THRESHOLDS_PX.skip * (1 + HYSTERESIS_FRACTION), // 0.55
  pointLower: LOD_THRESHOLDS_PX.point * (1 - HYSTERESIS_FRACTION), // 1.8
  pointUpper: LOD_THRESHOLDS_PX.point * (1 + HYSTERESIS_FRACTION), // 2.2
  bboxLineLower: LOD_THRESHOLDS_PX.bboxLine * (1 - HYSTERESIS_FRACTION), // 7.2
  bboxLineUpper: LOD_THRESHOLDS_PX.bboxLine * (1 + HYSTERESIS_FRACTION), // 8.8
} as const);

/**
 * Stateful classifier — given an entity's previous bucket, returns
 * the bucket for the current frame applying the 10% hysteresis band.
 *
 * `previous` may be `undefined` when this is the first classification;
 * we then fall through to the unguarded boundary classifier.
 */
export function lodBucketWithHysteresis(
  entity: RenderableEntity,
  cameraZoom: number,
  previous: LodBucket | undefined,
): LodBucket {
  // S9b (Codex round-7 P2 — "Keep annotation decorators lifted through viewport
  // cull"): DIMENSION / MULTILEADER always classify "full", never point/skip.
  // Their arrow / tick / synthesised-label meshes are BULK-LOAD-ONLY and the
  // per-frame line-only viewport-cull relift cannot rebuild them, so once lifted
  // they must stay in the drawn set — otherwise the first FrameLoops recompute
  // after fit-to-extents removes the line slot + marks them `lodHidden`, undoing
  // `partitionStubsByLod`'s initial-lift exemption and making the decorators that
  // were just preserved vanish until the entity crosses back into bbox-line/full.
  // Pinning "full" honours the SAME exemption in the per-frame visible-set path
  // (keeps them out of point/skip → not culled, not double-drawn in the points
  // cloud). Realistic decorator counts make always-full negligible and it is
  // AutoCAD-parity (annotations are never LOD-dropped). See isDecoratorEntityType.
  if (isDecoratorEntityType(entity.type)) return "full";
  const px = entityScreenSizePx(entity, cameraZoom);
  // Codex PR #784 round-10 P2 — the hysteresis band decides WHEN the bucket
  // moves; whether a zero-extent entity may be a points-cloud proxy at all is
  // the policy's call (`lodBucketForBounds`), applied to the sticky result too
  // so a stub cannot linger in `"point"` across frames either.
  return lodBucketForBounds({
    pixels: px,
    degenerate: isDegenerateBounds(entity.bounds),
    drawsDegenerateMarker: entityDrawsDegenerateMarker(entity),
    bucket: pixelsToBucketWithHysteresis(px, previous),
  });
}

/**
 * Pixel-driven variant of `lodBucketWithHysteresis`. Useful when the
 * caller already has the on-screen size cached.
 *
 * Algorithm:
 *
 *   1. Compute the unguarded bucket from `pixels`.
 *   2. If `previous` is undefined → return the unguarded bucket.
 *   3. If both pixels and previous map to the same band-side, return
 *      the unguarded bucket (no flicker risk).
 *   4. Otherwise check whether `pixels` is inside any of the three
 *      hysteresis bands. If yes — keep `previous` (sticky). If no —
 *      return the unguarded bucket (boundary clearly crossed).
 */
export function pixelsToBucketWithHysteresis(
  pixels: number,
  previous: LodBucket | undefined,
): LodBucket {
  const unguarded = lodBucketForPixels(pixels);
  if (previous === undefined) return unguarded;
  if (previous === unguarded) return unguarded;

  // Pixel is inside a band → keep the previous bucket. The band tests
  // are cheap; we test all three rather than picking the relevant one
  // because they are mutually exclusive ranges anyway.
  if (
    pixels >= HYSTERESIS_BANDS.skipLower &&
    pixels <= HYSTERESIS_BANDS.skipUpper
  ) {
    return previous;
  }
  if (
    pixels >= HYSTERESIS_BANDS.pointLower &&
    pixels <= HYSTERESIS_BANDS.pointUpper
  ) {
    return previous;
  }
  if (
    pixels >= HYSTERESIS_BANDS.bboxLineLower &&
    pixels <= HYSTERESIS_BANDS.bboxLineUpper
  ) {
    return previous;
  }
  // Outside all bands → boundary clearly crossed; trust the unguarded
  // classification.
  return unguarded;
}

/**
 * Per-entity LOD state. The renderer maintains one instance of this
 * map across frames; every camera change refreshes the bucket via
 * `lodBucketWithHysteresis(entity, zoom, prev?.bucket)`.
 *
 * Kept here (rather than re-purposing `RenderableEntity`) so the
 * renderable shape remains a pure value type that adapter tests can
 * round-trip without mutation.
 */
export interface LodEntityState {
  bucket: LodBucket;
  /**
   * Last camera zoom this entity was classified at — used by callers
   * who skip recomputation when the zoom is unchanged.
   */
  zoomAtClassification: number;
}

/**
 * Build a fresh state map for `entities` at the given zoom. No
 * hysteresis is applied (every entity is "first classification").
 */
export function buildInitialLodState(
  entities: ReadonlyArray<RenderableEntity>,
  cameraZoom: number,
): Map<string, LodEntityState> {
  const out = new Map<string, LodEntityState>();
  for (const entity of entities) {
    out.set(entity.id, {
      bucket: lodBucketWithHysteresis(entity, cameraZoom, undefined),
      zoomAtClassification: cameraZoom,
    });
  }
  return out;
}

/**
 * Refresh an existing state map in place at a new camera zoom,
 * applying hysteresis against each entity's previous bucket. Returns
 * the same map for convenience.
 *
 * Entities present in `entities` but absent from the map are added
 * (first-classification path); entities present in the map but absent
 * from `entities` are removed.
 */
export function refreshLodState(
  state: Map<string, LodEntityState>,
  entities: ReadonlyArray<RenderableEntity>,
  cameraZoom: number,
): Map<string, LodEntityState> {
  const seen = new Set<string>();
  for (const entity of entities) {
    seen.add(entity.id);
    const prev = state.get(entity.id);
    const bucket = lodBucketWithHysteresis(
      entity,
      cameraZoom,
      prev?.bucket,
    );
    state.set(entity.id, {
      bucket,
      zoomAtClassification: cameraZoom,
    });
  }
  for (const id of state.keys()) {
    if (!seen.has(id)) state.delete(id);
  }
  return state;
}
